'use client';

import Icon from '@/components/ui/AppIcon';

interface BookingSummaryProps {
  bookingDetails: any;
}

const BookingSummary = ({ bookingDetails }: BookingSummaryProps) => {
  const getServiceFee = () => {
    switch (bookingDetails?.type) {
      case 'flight':
        return 15;
      case 'hotel':
        return 20;
      case 'holiday':
        return 50;
      default:
        return 0;
    }
  };

  const basePrice = parseFloat(bookingDetails?.price || 0);
  const serviceFee = getServiceFee();
  const totalPrice = basePrice + serviceFee;

  return (
    <div className="bg-card rounded-lg shadow-brand p-6 sticky top-4">
      <h3 className="font-heading font-bold text-xl text-foreground mb-4 flex items-center gap-2">
        <Icon name="DocumentTextIcon" size={24} />
        Booking Summary
      </h3>

      <div className="space-y-4">
        {/* Booking Type */}
        <div className="pb-4 border-b border-border">
          <div className="flex items-center gap-2 mb-2">
            <Icon 
              name={bookingDetails?.type === 'flight' ? 'PaperAirplaneIcon' : 
                    bookingDetails?.type === 'hotel'? 'BuildingOfficeIcon' : 'GlobeAltIcon'} 
              size={20} 
              className="text-primary" 
            />
            <span className="font-bold text-foreground capitalize">
              {bookingDetails?.type} Booking
            </span>
          </div>

          {/* Flight Details */}
          {bookingDetails?.type === 'flight' && (
            <div className="text-sm text-muted-foreground space-y-1">
              <p><strong>From:</strong> {bookingDetails.from}</p>
              <p><strong>To:</strong> {bookingDetails.to}</p>
              <p><strong>Departure:</strong> {bookingDetails.departDate}</p>
              {bookingDetails.returnDate && (
                <p><strong>Return:</strong> {bookingDetails.returnDate}</p>
              )}
              {bookingDetails.airline && (
                <p><strong>Airline:</strong> {bookingDetails.airline}</p>
              )}
            </div>
          )}

          {/* Hotel Details */}
          {bookingDetails?.type === 'hotel' && (
            <div className="text-sm text-muted-foreground space-y-1">
              {bookingDetails.hotelName && (
                <p><strong>Hotel:</strong> {bookingDetails.hotelName}</p>
              )}
              <p><strong>Destination:</strong> {bookingDetails.destination}</p>
              <p><strong>Check-in:</strong> {bookingDetails.checkIn}</p>
              <p><strong>Check-out:</strong> {bookingDetails.checkOut}</p>
              <p><strong>Rooms:</strong> {bookingDetails.rooms}</p>
            </div>
          )}

          {/* Holiday Details */}
          {bookingDetails?.type === 'holiday' && (
            <div className="text-sm text-muted-foreground space-y-1">
              {bookingDetails.packageName && (
                <p><strong>Package:</strong> {bookingDetails.packageName}</p>
              )}
              <p><strong>Destination:</strong> {bookingDetails.destination}</p>
              {bookingDetails.duration && (
                <p><strong>Duration:</strong> {bookingDetails.duration}</p>
              )}
            </div>
          )}
        </div>

        {/* Price Breakdown */}
        <div className="space-y-3">
          <div className="flex justify-between text-foreground">
            <span>Base Price:</span>
            <span className="font-bold">£{basePrice.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-foreground">
            <span>Service Fee:</span>
            <span className="font-bold">£{serviceFee.toFixed(2)}</span>
          </div>
          <div className="border-t border-border pt-3 flex justify-between text-lg font-bold text-primary">
            <span>Total:</span>
            <span>£{totalPrice.toFixed(2)}</span>
          </div>
        </div>

        {/* Important Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
          <div className="flex items-start gap-2">
            <Icon name="InformationCircleIcon" size={20} className="text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-900">
              <p className="font-bold mb-1">What happens next?</p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li>Submit your booking details</li>
                <li>We'll contact you within 2 hours</li>
                <li>Complete payment securely</li>
                <li>Receive confirmation & tickets</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Contact Support */}
        <div className="text-center pt-4 border-t border-border">
          <p className="text-sm text-muted-foreground mb-2">Need help?</p>
          <a
            href="https://wa.me/447445270141"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-green-600 hover:text-green-700 font-bold text-sm"
          >
            <Icon name="ChatBubbleLeftRightIcon" size={16} />
            WhatsApp Support
          </a>
        </div>
      </div>
    </div>
  );
};

export default BookingSummary;
